
import Vue from "vue"
import { Component, Emit, Prop, Watch } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"

import { DateTime } from "luxon"

/* --- Types --- */
import { CaseAudit, Field, Template, TimeZone } from "@/types"
import { AuditTrailEntry, FieldReassignmentAuditItem, reassignmentAuditSupportedBuiltInCaseColumnKeys, reassignmentAuditSupportedDataTypes } from "@/types/fieldReassignmentAudit"
import { TenantFeatures } from "@/types/tenantFeatures"
import { CaseColumn, getBuiltInCaseColumn, isBuiltInCaseColumn } from "@/types/caseColumns"
import { FieldTypeId } from "@/types/fieldTypes"
import { UserSettings, ViewsOpeningOption } from "@/types/userSettings"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import { SortDirection } from "@/types/sortDirection"
import { TenantSettingKeys } from "@/types/tenantSetting"

/* --- Components --- */
import SkeletonLoader from "../../SkeletonLoader.vue"
import FieldReassignmentAuditTrail from "./FieldReassignmentAuditTrail.vue"
// @ts-ignore
import VueSelect from "vue-select"
// @ts-ignore
import VDatePicker from "v-calendar/lib/components/date-picker.umd"

/* --- Services --- */
import api from "@/services/api"

export interface FieldReassignmentAuditSettings {
	threshold: number
	fieldIds: string[]
	defaultDayRange: number
	includeForecasted: boolean
}

interface TableColumnData {
	key: string
	label: string
	isSortable: boolean
	sortValueFn?: (value: any) => string
}

const setup = namespace("setup")
const cases = namespace("cases")

@Component({
	components: {
		"v-select": VueSelect,
		"skeleton": SkeletonLoader,
		"date-picker": VDatePicker,
		"audit-trail": FieldReassignmentAuditTrail
	}
})
export default class FieldReassignmentAudit extends Vue {
	/* --- Props --- */

	@Prop({ type: Object, required: true }) private value!: FieldReassignmentAuditSettings
	@Prop({ type: Boolean, required: true }) private edit!: boolean

	/* --- Vuex --- */

	@State displayTimezone!: TimeZone | null
	@Getter private getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter private getCaseName!: () => string
	@Getter("getUserSettings") private userSettings!: UserSettings;
	@Getter("getTenantSettings") private tenantSettings!: { [key: string]: any } | null

	@setup.Getter private getAccessibleFields!: CaseColumn[]
	@setup.Action private loadFieldsIfUnloaded!: () => Promise<Field[]>

	@cases.State private categories!: Template[]
	@cases.Action private refreshCategories!: () => Promise<void>

	/* --- Constants --- */

	private defaultDayRange: number = 31
	private filterDebounceMs: number = 500

	/* --- Data --- */

	private isLoadingMounted = false
	private isLoadingData = false

	private dateRange: { start: Date, end: Date } = {
		start: new Date(),
		end: new Date()
	}

	private lastResultsFetchRequestedAt: Date | null = null
	private lastFetchFailed: boolean = false

	private results: FieldReassignmentAuditItem[] = []

	// sorting and filtering
	private sortColumn: TableColumnData | null = null
	private sortDirection: SortDirection = SortDirection.Ascending
	private filterQuery: string = ""

	/* --- Computed Properties --- */

	/**
	 * @summary Returns the columns to display in the table.
	 */
	private get tableColumns (): TableColumnData[] {
		return [
			{
				key: "caseNumber",
				label: `${this.getCaseName} #`,
				isSortable: true
			},
			{
				key: "caseTitle",
				label: `${this.getCaseName} Title`,
				isSortable: true
			},
			{
				key: "caseCreationDate",
				label: `${this.getCaseName} Created`,
				isSortable: true
			},
			{
				key: "caseTemplateId",
				label: "Template",
				isSortable: true,
				sortValueFn: (templateId: string) => this.getCategoryTitle(templateId)
			},
			{
				key: "fieldId",
				label: "Field",
				isSortable: true,
				sortValueFn: (fieldId: string) => this.getFieldTitle(fieldId)
			},
			{
				key: "assignmentCount",
				label: "Field Assignment Count",
				isSortable: true
			},
			{
				key: "lastAssignmentAt",
				label: "Last Assignment At",
				isSortable: true
			},
			{
				key: "auditTrail",
				label: "Audit Trail",
				isSortable: false
			}
		]
	}

	private get tileSettings (): FieldReassignmentAuditSettings {
		return this.value
	}

	/**
	 * @summary Returns the available supported field options for the field selection dropdown.
	 */
	private get fieldOptions (): { value: string, label: string }[] {
		const tenancyAuditFieldOptions = this.tenantSettings
			? this.tenantSettings[TenantSettingKeys.ReassignmentAuditFields] ?? []
			: []

		if (!tenancyAuditFieldOptions || tenancyAuditFieldOptions.length === 0) {
			return []
		}

		return this.getAccessibleFields.filter(field =>
			tenancyAuditFieldOptions.includes(field.key) &&
			(field.key.startsWith("fields.") || reassignmentAuditSupportedBuiltInCaseColumnKeys.includes(field.key)) &&
			reassignmentAuditSupportedDataTypes.includes(field.dataType as FieldTypeId)
		).map(field => ({ value: field.key, label: field.label }))
	}

	private get isMultiSelectKeepOpenFeatureEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.MultiSelectKeepOpen)
	}

	private get isLoading (): boolean {
		return this.isLoadingMounted || this.isLoadingData
	}

	/**
	 * @summary Returns whether the tile is properly configured.
	 */
	private get isTileConfigured (): boolean {
		return this.value.fieldIds && this.value.fieldIds.length > 0
	}

	/**
	 * @summary Returns whether the table should be shown based on the current state of the tile.
	 */
	private get shouldShowTable (): boolean {
		return this.isTileConfigured && !this.lastFetchFailed && this.filteredAndSortedResults.length > 0
	}

	/**
	 * @summary Returns the results filtered based on the current filter query and sorted based on the
	 * current sort column and direction.
	 */
	private get filteredAndSortedResults (): FieldReassignmentAuditItem[] {
		let results = this.results

		if (this.filterQuery) {
			const queryLower = this.filterQuery.toLowerCase()

			results = results.filter(result => {
				return result.caseNumber.toLowerCase().includes(queryLower) ||
					result.caseTitle.toLowerCase().includes(queryLower) ||
					this.getCategoryTitle(result.caseTemplateId).toLowerCase().includes(queryLower) ||
					result.isForecastedCase.toString().includes(queryLower) ||
					formatDateMixin.methods.displayDateFromISOString(new Date(result.caseCreationDate).toISOString()).includes(queryLower) ||
					this.getFieldTitle(result.fieldId).toLowerCase().includes(queryLower) ||
					result.assignmentCount.toString().includes(queryLower) ||
					formatDateMixin.methods.displayDateFromISOString(new Date(result.lastAssignmentAt).toISOString()).includes(queryLower) ||
					this.auditTrail[this.getCaseFieldCompositeKey(result.caseNumber, result.fieldId)].some(trailEntry =>
						trailEntry.changedAt.toLowerCase().includes(queryLower) ||
						trailEntry.changedBy.toLowerCase().includes(queryLower) ||
						trailEntry.oldValue?.toLowerCase().includes(queryLower) ||
						trailEntry.newValue?.toLowerCase().includes(queryLower)
					)
			})
		}

		if (this.sortColumn) {
			const sortColumn = this.sortColumn
			const sortDirection = this.sortDirection

			return results.sort((a, b) => {
				let aValue = a[sortColumn.key as keyof FieldReassignmentAuditItem]
				let bValue = b[sortColumn.key as keyof FieldReassignmentAuditItem]

				if (sortColumn.sortValueFn) {
					aValue = sortColumn.sortValueFn(aValue)
					bValue = sortColumn.sortValueFn(bValue)
				}

				if (aValue === bValue) return 0
				if (sortDirection === SortDirection.Ascending) {
					return aValue > bValue ? 1 : -1
				} else {
					return aValue < bValue ? 1 : -1
				}
			})
		}

		return results
	}

	/**
	 * @summary Returns formatted audit trail data for each case field.
	 */
	private get auditTrail (): { [caseIdAndFieldId: string]: AuditTrailEntry[] } {
		if (!this.results) return {}

		const timeZoneHook = this.displayTimezone

		const resultTrail: { [caseIdAndFieldId: string]: AuditTrailEntry[] } = {}
		for (const result of this.results) {
			const isCaseColumn = isBuiltInCaseColumn(result.fieldId)
			const fullFieldId = isCaseColumn ? result.fieldId : "fields." + result.fieldId
			const field = this.getAccessibleFields.find(f => f.key === fullFieldId) ?? null
			resultTrail[this.getCaseFieldCompositeKey(result.caseNumber, result.fieldId)] =
				this.getAuditTrailEntries(result.assignmentAudits, field)
		}

		return resultTrail
	}

	/**
	 * @summary Returns whether the tile has a date range set.
	 */
	private get hasDateRange (): boolean {
		return this.dateRange && !!this.dateRange.start && !!this.dateRange.end
	}

	/**
	 * @summary Returns the tile's current date range adjusted to the display timezone.
	 */
	private get adjustedDateRange (): { start: DateTime, end: DateTime } {
		const timeZone = this.displayTimezone

		return {
			start: DateTime.fromJSDate(this.dateRange.start, { zone: "UTC" })
				.setZone(timeZone?.iana ?? "local", { keepLocalTime: true }),
			end: DateTime.fromJSDate(this.dateRange.end, { zone: "UTC" })
				.setZone(timeZone?.iana ?? "local", { keepLocalTime: true })
		}
	}

	/* --- Vue-related Methods --- */

	private async mounted (): Promise<void> {
		this.isLoadingMounted = true

		// load required data
		const fieldsLoadPromise = this.loadFieldsIfUnloaded()
		const categoriesLoadPromise = !(this.categories?.length > 0)
			? this.refreshCategories()
			: Promise.resolve()

		// set default tile settings if none exist
		if (!this.value || Object.keys(this.value).length === 0) {
			this.setDefaultTileSettings()
		}

		await Promise.allSettled([fieldsLoadPromise, categoriesLoadPromise])

		this.setDefaultDateRange()

		this.isLoadingMounted = false

		if (!this.edit) {
			this.loadResults()
		}
	}

	@Emit("input")
	private updateTileSetting (setting: string, newValue: any): FieldReassignmentAuditSettings {
		return { ...this.value, [setting]: newValue }
	}

	@Watch("edit")
	private onEditModeChanged (isEditMode: boolean): void {
		if (!isEditMode) {
			this.setDefaultDateRange()
			this.loadResults()
		}
	}

	/* --- Methods --- */

	/**
	 * @summary Fetches results from the API based on the current parameters.
	 * @param rateLimit Whether to rate limit the fetch request.
	 */
	private async loadResults (rateLimit: boolean = false): Promise<void> {
		if (!this.value || !this.isTileConfigured) {
			this.results = []
			return
		}

		// rate limit if appropriate
		const lastFetchRequestAt = this.lastResultsFetchRequestedAt
		if (rateLimit && lastFetchRequestAt && lastFetchRequestAt > new Date(Date.now() - 400)) {
			return
		}

		this.isLoadingData = true
		const thisFetchRequestedAt = new Date(Date.now())
		this.lastResultsFetchRequestedAt = thisFetchRequestedAt

		let fetchedData: FieldReassignmentAuditItem[] = []
		try {
			fetchedData = await api.fieldReassignmentAudit(
				this.value.threshold,
				this.value.includeForecasted,
				this.value.fieldIds,
				this.adjustedDateRange.start.toJSDate().toISOString(),
				this.adjustedDateRange.end.toJSDate().toISOString()
			)

			this.lastFetchFailed = false
		} catch (e) {
			console.error("Error fetching field reassignment audit data", e)
			this.lastFetchFailed = true
		}

		if (thisFetchRequestedAt !== this.lastResultsFetchRequestedAt) return

		this.resetFilter()
		this.resetSort()

		this.results = fetchedData

		this.isLoadingData = false
	}

	/**
	 * @summary Generates a unique composite key for a case field.
	 * @param caseNumber The case number.
	 * @param fieldId The field ID.
	 */
	private getCaseFieldCompositeKey (caseNumber: string, fieldId: string): string {
		return `${caseNumber},${fieldId}`
	}

	/**
	 * @summary Formats the audit trail entries for display.
	 * @param caseFieldAudits The audits for a specific case field.
	 * @param field The field that the audits are for.
	 */
	private getAuditTrailEntries (caseFieldAudits: CaseAudit[], field: CaseColumn | null): AuditTrailEntry[] {
		return caseFieldAudits.map(audit => ({
			changedAt: formatDateMixin.methods.displayDateFromISOString(audit.changedAt),
			changedBy: audit.changedBy?.name ?? "(Unknown)",
			oldValue: this.formatFieldValue(audit.oldValueLabel ?? audit.oldValue, field?.dataType as FieldTypeId),
			newValue: this.formatFieldValue(audit.newValueLabel ?? audit.newValue, field?.dataType as FieldTypeId)
		} as AuditTrailEntry))
	}

	/**
	 * @summary Formats a field value for display based on its data type.
	 * @param value The value to format.
	 * @param dataType The data type of the field.
	 */
	private formatFieldValue (value: string | null, dataType: FieldTypeId): string | null {
		if (value === null || value.length === 0) return null

		switch (dataType) {
			case FieldTypeId.Text:
			case FieldTypeId.TextArea:
				return `"${value}"`
			case FieldTypeId.Date:
			case FieldTypeId.DateTime:
				return formatDateMixin.methods.displayDateFromISOString(value)
			case FieldTypeId.Checkbox:
				return value[0].toUpperCase() + value.slice(1)
			case FieldTypeId.Dollar:
				return `$${value}`
			default:
				return value
		}
	}

	private updateIncludeForecastedTileSetting (newValue: boolean): void {
		this.updateTileSetting("includeForecasted", newValue)
	}

	private updateThresholdTileSetting (newValue: number): void {
		const newValNumber = typeof newValue === "number"
			? newValue
			: parseInt(newValue)

		this.updateTileSetting("threshold", newValNumber)
	}

	private updateFieldIdsTileSetting (newValue: string[]): void {
		this.updateTileSetting("fieldIds", newValue)
	}

	private updateDefaultDayRangeSetting (newValue: number): void {
		this.updateTileSetting("defaultDayRange", newValue)
	}

	/**
	 * @summary Sets the tile's settings to the default values.
	 */
	private setDefaultTileSettings (): void {
		this.$emit("input", {
			threshold: 1,
			fieldIds: [],
			defaultDayRange: this.defaultDayRange,
			includeForecasted: true
		})
	}

	private getFieldTitle (fieldId: string): string {
		const isCaseColumn = isBuiltInCaseColumn(fieldId)
		return isCaseColumn
			? getBuiltInCaseColumn(fieldId)!.label
			: this.getAccessibleFields.find(f => f.key === "fields." + fieldId)?.label ?? fieldId
	}

	private getCategoryTitle (categoryId: string): string {
		return this.categories?.find(c => c.id === categoryId)?.title ?? categoryId
	}

	/**
	 * @summary Handles a click on a row in the table.
	 * @param caseNumber The case number to open.
	 * @param isCtrlClick Whether the click was a ctrl-click.
	 */
	private onRowClicked (caseNumber: string, isCtrlClick: boolean = false): void {
		this.openCase(caseNumber, isCtrlClick)
	}

	/**
	 * @summary Opens a case in a new tab or the same tab based on user settings.
	 * @param caseNumber The case number to open.
	 * @param useAlternativeOpenMethod Whether to use the alternative open method to the user's default.
	 */
	private openCase (caseNumber: string, useAlternativeOpenMethod: boolean = false): void {
		const encodedEntityIdentifier = encodeURIComponent(caseNumber)

		let shouldOpenCaseInNewTab = this.userSettings?.settings?.viewsOpening !== ViewsOpeningOption.SameTab

		if (useAlternativeOpenMethod) shouldOpenCaseInNewTab = !shouldOpenCaseInNewTab

		if (shouldOpenCaseInNewTab) {
			const routeData = this.$router.resolve({ path: `/cases/${encodedEntityIdentifier}` })
			window.open(routeData.href, "_blank")
		} else {
			this.$router.push({ path: `/cases/${encodedEntityIdentifier}` })
		}
	}

	/**
	 * @summary Handles a click on a table column header to sort the table.
	 * @param column The column to sort by.
	 */
	private sortTable (column: TableColumnData): void {
		if (!column.isSortable) return

		if (this.sortColumn === column) {
			this.sortDirection = this.sortDirection === SortDirection.Ascending
				? SortDirection.Descending
				: SortDirection.Ascending
		} else {
			this.sortColumn = column
			this.sortDirection = SortDirection.Ascending
		}
	}

	private resetSort (): void {
		this.sortColumn = null
		this.sortDirection = SortDirection.Ascending
	}

	private resetFilter (): void {
		this.filterQuery = ""
	}

	/**
	 * @summary Handles a change in the date range.
	 * @param dateRange The new date range.
	 * @param preventFetch Whether to prevent fetching new data after the date range change.
	 */
	private async dateRangeChanged (dateRange: { start: Date, end: Date }, preventFetch?: boolean): Promise<void> {
		const tzAdjustedDateRange = {
			start: DateTime.fromJSDate(dateRange.start).toUTC(undefined, { keepLocalTime: false }).startOf("day").toJSDate(),
			end: DateTime.fromJSDate(dateRange.end).toUTC(undefined, { keepLocalTime: false }).endOf("day").toJSDate()
		}

		if (tzAdjustedDateRange.start.getTime() === this.dateRange.start.getTime() &&
			tzAdjustedDateRange.end.getTime() === this.dateRange.end.getTime()) {
			// VCalendar sometimes emits an input event even if the new date was set by the parent component
			return
		}

		this.dateRange = tzAdjustedDateRange

		if (!preventFetch) {
			await this.$nextTick()
			this.loadResults()
		}
	}

	private addDaysToDate (date: Date, days: number): Date {
		const result = new Date(date)
		result.setTime(result.getTime() + days * 24 * 3600 * 1000)
		return result
	}

	/**
	 * @summary Sets the tile's date range to the default range set in the tile's settings.
	 */
	private setDefaultDateRange (): void {
		const dateRange = {
			start: this.addDaysToDate(new Date(), -(this.tileSettings.defaultDayRange ?? this.defaultDayRange)),
			end: new Date()
		}

		this.dateRangeChanged(dateRange, true)
	}
}
